import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BellOutlined } from '@ant-design/icons';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseBadge } from '@app/components/common/BaseBadge/BaseBadge';
import { HeaderActionWrapper } from '@app/components/header/Header.styles';
import { BasePopover } from '@app/components/common/BasePopover/BasePopover';
import { AppNotification, getNotificationsApi, markAllReadApi, markOneReadApi } from '@app/api/notifications.api';
import { NotificationsOverlay } from './NotificationsOverlay/NotificationsOverlay';

const POLL_INTERVAL_MS = 30_000;

export const NotificationsDropdown: React.FC = () => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpened, setOpened] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNotifications = useCallback(async () => {
    const result = await getNotificationsApi();
    if (result) {
      setNotifications(result.notifications);
      setUnreadCount(result.unread_count);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchNotifications]);

  const handleMarkAllRead = async () => {
    await markAllReadApi();
    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleMarkOneRead = async (id: number): Promise<void> => {
    await markOneReadApi(id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  return (
    <BasePopover
      trigger="click"
      content={
        <NotificationsOverlay
          notifications={notifications}
          onMarkAllRead={handleMarkAllRead}
          onMarkOneRead={handleMarkOneRead}
        />
      }
      onOpenChange={(open) => {
        setOpened(open);
        if (open) fetchNotifications();
      }}
    >
      <HeaderActionWrapper>
        <BaseButton
          type={isOpened ? 'ghost' : 'text'}
          icon={
            <BaseBadge count={unreadCount} size="small" overflowCount={99}>
              <BellOutlined />
            </BaseBadge>
          }
        />
      </HeaderActionWrapper>
    </BasePopover>
  );
};
